import {isFeatureEnabled} from 'feature-flags';
import {CreatableMultiChoice, FormControl, MultiChoice} from 'platform/components';

import {useEffect} from 'react';
import {FieldValues, Path, useController} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {useGetTopMakesQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  getVehicleMakes,
  selectVehicleCatalogue,
  selectVehicleMakes,
  useApiDispatch,
  VehicleCatalogueMakeModelItem,
  VehicleType,
} from '@dms/teas';

import {TestIdProps} from 'shared/web';

type TopMake = {
  code: string;
};

type MakeSelectFieldProps<TFieldValues extends FieldValues> = {
  control: FormControl<TFieldValues>;
  name: Path<TFieldValues>;
  label?: string;
  vehicleType: VehicleType;
  isDisabled?: boolean;
  closeMenuOnSelect?: boolean;
  hasOptionCheckbox?: boolean;
  handleCreateOption?: (vehicleType: string, value: string) => void;
} & TestIdProps;

export function MakeSelectField<TFieldValues extends FieldValues>(
  props: MakeSelectFieldProps<TFieldValues>
) {
  const apiDispatch = useApiDispatch();
  const {loading} = getVehicleMakes.useThunkState(selectVehicleCatalogue);
  const makeOptions = useSelector(
    selectVehicleMakes(props.vehicleType)
  ) as VehicleCatalogueMakeModelItem[];
  const {data: topMakes} = useGetTopMakesQuery();

  const {field, fieldState} = useController({
    name: props.name,
    control: props.control,
  });

  const topMakeOptions = makeOptions
    .filter((make) => topMakes?.find((topMake: TopMake) => make.value === topMake.code))
    .map((make) => ({value: make.value, label: make.label}));

  const otherMakeOptions = makeOptions
    .filter((make) => !topMakes?.find((topMake: TopMake) => make.value === topMake.code))
    .map((make) => ({value: make.value, label: make.label}));

  const groupedOptions = [
    {label: i18n.t('general.labels.topMakes'), options: topMakeOptions},
    {label: i18n.t('general.labels.allMakes'), options: otherMakeOptions},
  ];

  useEffect(
    function loadVehicleMakes() {
      apiDispatch(getVehicleMakes.action, {vehicleType: props.vehicleType}, {throwOff: true});
    },
    [props.vehicleType, apiDispatch]
  );

  const handleCreate = (inputValue: string) => {
    props.handleCreateOption?.(props.vehicleType, inputValue);
  };

  const fieldValue = (field.value ?? []) as string[];

  const isCreatable = isFeatureEnabled(featureFlags.SALES_CUSTOM_MAKE_MODEL);

  if (isCreatable) {
    return (
      <CreatableMultiChoice<string>
        label={props.label}
        value={fieldValue}
        onChange={field.onChange}
        onBlur={field.onBlur}
        options={groupedOptions}
        isLoading={loading}
        isDisabled={props.isDisabled}
        closeMenuOnSelect={props.closeMenuOnSelect}
        hasOptionCheckbox={props.hasOptionCheckbox}
        isInvalid={!!fieldState.error}
        errorMessage={fieldState.error?.message}
        onCreateOption={handleCreate}
        formatCreateLabel={(inputValue: string) => `${i18n.t('general.actions.add')} ${inputValue}`}
        data-testid={props['data-testid']}
      />
    );
  }

  return (
    <MultiChoice<string>
      label={props.label}
      value={fieldValue}
      onChange={field.onChange}
      onBlur={field.onBlur}
      options={groupedOptions}
      isLoading={loading}
      isDisabled={props.isDisabled}
      closeMenuOnSelect={props.closeMenuOnSelect}
      hasOptionCheckbox={props.hasOptionCheckbox}
      isInvalid={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      data-testid={props['data-testid']}
    />
  );
}
